/**
 * Sprint 11.12 — Merged "Client Report" tab.
 *
 * Replaces the previously separate "AI Report" + "Client Links" tabs with
 * a single surface organised around the actual workflow:
 *
 *    1. HR sees an in-app preview of the same magazine layout the client
 *       will see (live data, no email gate, all artefacts unlocked).
 *    2. A primary "Share with client" button opens the existing
 *       generate-link dialog (with strict-readiness gating + 2-column
 *       layout).
 *    3. A collapsed "Share history & access" section at the bottom lists
 *       all previously issued share links — only expanded on click.
 *
 * The magazine renderer (`MagazineReport`) is the same component used by
 * `/client-report-demo` and the public `/client/:token/report` route.
 */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight, Send, Link as LinkIcon, Eye } from 'lucide-react';
import { v1Get, v1Post } from '@/lib/api';
import { MagazineReport } from '@/components/clientReport';
import { ClientShareLinksTab, CreateLinkDialog } from '@/components/ClientShareLinksTab';
import { adaptLivePayloadToMagazine } from '@/lib/clientReportAdapter';
import { GENERIC_EVAL_FAILURE_MSG } from '@/lib/evalErrors';
import { toast } from 'sonner';
import { useHasPermission, Gated } from '@/auth/permissions';

/** Keep videobot presigned URLs stable across recommendation polls (same question_id). */
function preserveVideobotUrlsOnPreviewPoll(oldData, newData) {
  if (!oldData || !newData) return newData;
  const oldChapters = oldData.videobot_chapters || [];
  const newChapters = newData.videobot_chapters || [];
  if (!oldChapters.length || !newChapters.length) return newData;

  const urlByQuestion = Object.fromEntries(
    oldChapters
      .filter((ch) => ch.question_id && ch.video_url)
      .map((ch) => [ch.question_id, ch.video_url]),
  );

  let changed = false;
  const merged = newChapters.map((ch) => {
    const preserved = urlByQuestion[ch.question_id];
    if (preserved && preserved !== ch.video_url) {
      changed = true;
      return { ...ch, video_url: preserved };
    }
    return ch;
  });

  return changed ? { ...newData, videobot_chapters: merged } : newData;
}

function isForbiddenError(err) {
  return err?.response?.status === 403;
}

// =========================================================================
// Component
// =========================================================================
export function ClientReportTab({ candidateId, evaluationId = null }) {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const canViewReport = useHasPermission('report.view_internal');
  const canShareExternal = useHasPermission('report.share_external');
  const canEditEvaluation = useHasPermission('evaluation.edit');
  const canShareReport = canShareExternal;

  // P1 #7 — invalidate artefact-readiness when HR opens the dialog so the
  // server-side gate doesn't disagree with stale FE checkboxes.
  const openDialog = () => {
    if (!evaluationId || !canShareReport) return;
    qc.invalidateQueries({ queryKey: ['v1-artefact-readiness', candidateId, evaluationId] });
    setDialogOpen(true);
  };

  const previewQs = evaluationId
    ? `?evaluation_id=${encodeURIComponent(evaluationId)}`
    : '';

  // 1. Magazine preview data — poll while recommendation is pending
  const previewQ = useQuery({
    queryKey: ['v1-client-report-preview', candidateId, evaluationId],
    queryFn: () => v1Get(`/candidates/${candidateId}/client-report-preview${previewQs}`),
    enabled: !!candidateId && !!evaluationId && canViewReport,
    staleTime: 10_000,
    refetchInterval: (query) => {
      const status = query.state.data?.recommendation?.status;
      return status === 'pending' ? 4000 : false;
    },
    structuralSharing: preserveVideobotUrlsOnPreviewPoll,
  });

  // 2. Issued share links (for the counter + collapsible history below)
  const linksQ = useQuery({
    queryKey: ['v1-share-links', candidateId],
    queryFn: () => v1Get(`/candidates/${candidateId}/client-share-links`),
    enabled: !!candidateId && !!evaluationId && canShareReport,
    staleTime: 10_000,
  });

  const regenerateMut = useMutation({
    mutationFn: () => v1Post(
      `/candidates/${candidateId}/recommendation/regenerate${previewQs}`,
      {},
    ),
    onSuccess: (data) => {
      const status = data?.recommendation?.status;
      if (status === 'awaiting_evidence' || status === 'awaiting_final_call') {
        toast.error(
          data?.recommendation?.error_text
          || 'Cannot generate recommendation yet.',
        );
      } else {
        toast.success(
          status === 'pending' ? 'Recommendation generation queued' : 'Recommendation updated',
        );
      }
      qc.invalidateQueries({ queryKey: ['v1-client-report-preview', candidateId] });
    },
    onError: () => toast.error(GENERIC_EVAL_FAILURE_MSG),
  });

  const magazine = useMemo(() => (
    previewQ.data ? adaptLivePayloadToMagazine(previewQ.data) : null
  ), [previewQ.data]);

  const links = linksQ.data || [];
  const activeCount = links.filter((l) => l.status === 'active').length;
  const totalCount = links.length;

  if (!canViewReport) {
    return (
      <Card className="card-elevated" data-testid="client-report-tab">
        <CardContent className="p-6" data-testid="client-report-permission-denied">
          <p className="text-sm font-semibold">Access denied</p>
          <p className="mt-1 text-sm text-muted-foreground">
            You don’t have permission to view the client report.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!evaluationId) {
    return (
      <Card className="card-elevated" data-testid="client-report-tab">
        <CardContent className="p-6 text-sm text-muted-foreground">
          Select an evaluation to preview the client report.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="client-report-tab">
      {/* ====== Header bar ====== */}
      <Card className="card-elevated">
        <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <p className="text-sm font-semibold flex items-center gap-2">
              <Eye className="h-4 w-4 text-primary" />
              Client report preview
            </p>
            <p className="text-xs text-muted-foreground">
              Same magazine layout the client gets (no email gate, all artefacts), plus staff-only
              Recommendation (section 8). Latest company resume appears here even before Approve;
              clients only receive an approved resume.
              {canShareReport ? (
                <>
                  {' '}Use
                  <strong className="text-foreground"> Share with client</strong> to issue a gated link.
                </>
              ) : (
                <> You don’t have permission to share this report externally.</>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canShareReport && totalCount > 0 && (
              <Badge variant="outline" className="gap-1.5" data-testid="client-report-active-links-badge">
                <LinkIcon className="h-3 w-3" />
                {activeCount} active {totalCount > activeCount ? `\u00b7 ${totalCount - activeCount} expired/revoked` : ''}
              </Badge>
            )}
            <Gated need="report.share_external">
              <Button
                onClick={openDialog}
                className="gap-1.5"
                data-testid="client-report-share-btn"
              >
                <Send className="h-4 w-4" />
                Share with client
              </Button>
            </Gated>
          </div>
        </CardContent>
      </Card>

      {/* ====== Magazine preview ====== */}
      {previewQ.isLoading && (
        <Card className="card-elevated"><CardContent className="p-8 text-sm text-muted-foreground" data-testid="client-report-tab-loading">
          Loading client report preview…
        </CardContent></Card>
      )}
      {previewQ.isError && (
        <Card className="card-elevated">
          <CardContent className="p-8 text-sm" data-testid="client-report-tab-error">
            {isForbiddenError(previewQ.error) ? (
              <>
                <p className="font-semibold text-foreground">Access denied</p>
                <p className="mt-1 text-muted-foreground">
                  You don’t have permission to view the client report.
                </p>
              </>
            ) : (
              <p className="text-rose-700">
                Could not load the report preview. Please try again.
              </p>
            )}
          </CardContent>
        </Card>
      )}
      {magazine && (
        <div className="-mx-2 rounded-2xl border bg-secondary/20 p-2 sm:-mx-0 sm:p-0 sm:bg-transparent sm:border-0" data-testid="client-report-tab-preview-wrapper">
          <MagazineReport
            data={magazine}
            isDemo={false}
            onRegenerateRecommendation={canEditEvaluation ? () => regenerateMut.mutate() : null}
          />
        </div>
      )}

      {/* ====== Collapsed share history & access logs ====== */}
      <Gated need="report.share_external">
        <Card className="card-elevated">
          <button
            type="button"
            onClick={() => setHistoryOpen((v) => !v)}
            className="flex w-full items-center justify-between gap-2 p-4 text-left hover:bg-secondary/30"
            data-testid="client-report-history-toggle"
            aria-expanded={historyOpen}
          >
            <div className="flex items-center gap-2">
              {historyOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
              <span className="text-sm font-semibold">Share history & access logs</span>
              <Badge variant="outline" className="text-[10px]" data-testid="client-report-history-count">
                {totalCount} link{totalCount === 1 ? '' : 's'}
              </Badge>
            </div>
            <span className="text-[11px] text-muted-foreground">{historyOpen ? 'Hide' : 'Show'}</span>
          </button>
          {historyOpen && (
            <div className="border-t" data-testid="client-report-history-panel">
              <ClientShareLinksTab candidateId={candidateId} evaluationId={evaluationId} />
            </div>
          )}
        </Card>
      </Gated>

      {/* ====== Generate-link dialog (shared with the embedded history view) ====== */}
      {canShareReport && (
        <CreateLinkDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          candidateId={candidateId}
          evaluationId={evaluationId}
        />
      )}
    </div>
  );
}

export default ClientReportTab;
